import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Typography, Button, Modal, Form, Input, Select, Switch, Tag, Space,
  Alert, Row, Col, Tooltip, EmptyState, Table, Divider, theme, message, Drawer,
} from '../../ui';
import type { TagTone } from '../../ui';
import {
  Plus, Pencil, FlaskConical, Trash2, CheckCircle2, Ban, Copy, Info,
} from 'lucide-react';
import { request } from '../../api/client';
import { TwDataTable as DataTable, DataTableColumn } from '../../components/TwDataTable';

interface Variable { key: string; label: string; example: string; }
interface Trigger {
  code: string;
  name: string;
  description: string;
  category: string;
  variables: Variable[];
  buttonByDefault?: boolean;
}

interface WaTemplate {
  id: string;
  triggerEvent: string;
  metaTemplateName: string;
  language: string;
  bodyPreview: string;
  variableOrder: string[];
  hasButton: boolean;
  buttonUsesFirstParam: boolean;
  isActive: boolean;
  notes?: string;
  createdAt: string;
}

const LANG_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'hi', label: 'हिंदी (Hindi)' },
  { value: 'mr', label: 'मराठी (Marathi)' },
  { value: 'bn', label: 'বাংলা (Bengali)' },
  { value: 'or', label: 'ଓଡ଼ିଆ (Odia)' },
];

const CATEGORY_COLOR: Record<string, TagTone> = {
  Auth: 'red', Sales: 'blue', Billing: 'gold', Logistics: 'cyan',
  Service: 'purple', Marketing: 'green',
};

export function WaTemplatesPage() {
  const { token } = theme.useToken();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<WaTemplate | null>(null);
  const [helperFor, setHelperFor] = useState<string | undefined>();
  const [testFor, setTestFor] = useState<WaTemplate | null>(null);

  const { data: triggers } = useQuery<Trigger[]>({
    queryKey: ['wa-triggers'],
    queryFn: () => request<Trigger[]>({ url: '/wa-templates/triggers' }),
  });

  const { data: templates, isLoading } = useQuery<WaTemplate[]>({
    queryKey: ['wa-templates'],
    queryFn: () => request<WaTemplate[]>({ url: '/wa-templates' }),
  });

  const save = useMutation({
    mutationFn: (vals: any) =>
      editing
        ? request({ url: `/wa-templates/${editing.id}`, method: 'PUT', data: vals })
        : request({ url: '/wa-templates', method: 'POST', data: vals }),
    onSuccess: () => {
      message.success(editing ? 'Updated' : 'Created');
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
      setOpen(false);
      setEditing(null);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const toggle = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      request({
        url: `/wa-templates/${id}/${active ? 'activate' : 'deactivate'}`,
        method: 'PATCH',
      }),
    onSuccess: () => {
      message.success('Status updated');
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => request({ url: `/wa-templates/${id}`, method: 'DELETE' }),
    onSuccess: () => {
      message.success('Deleted');
      qc.invalidateQueries({ queryKey: ['wa-templates'] });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setOpen(true);
  };
  const openEdit = (row: WaTemplate) => {
    setEditing(row);
    setOpen(true);
  };

  const columns: DataTableColumn<WaTemplate>[] = [
    {
      key: 'triggerEvent', title: 'Trigger', dataIndex: 'triggerEvent',
      sortable: true, alwaysVisible: true,
      render: (v: string) => {
        const t = triggers?.find((tt) => tt.code === v);
        return (
          <div>
            <div style={{ fontWeight: 600 }}>{t?.name ?? v}</div>
            <Tag tone={CATEGORY_COLOR[t?.category ?? ''] ?? 'neutral'} style={{ marginTop: 2 }}>
              {t?.category ?? '—'}
            </Tag>
          </div>
        );
      },
    },
    {
      key: 'metaTemplateName', title: 'Meta template', dataIndex: 'metaTemplateName',
      sortable: true,
      render: (v: string) => <Typography.Text code>{v}</Typography.Text>,
    },
    {
      key: 'language', title: 'Lang', dataIndex: 'language',
      sortable: true, width: 90,
      render: (v: string) => <Tag>{v.toUpperCase()}</Tag>,
    },
    {
      key: 'variableOrder', title: 'Variables', dataIndex: 'variableOrder',
      render: (vs: string[]) => (
        <Space wrap size={2}>
          {(vs ?? []).map((k, i) => (
            <Tag key={k} tone="blue">{`{{${i + 1}}}`} {k}</Tag>
          ))}
          {(vs ?? []).length === 0 && <span style={{ color: token.colorTextTertiary }}>—</span>}
        </Space>
      ),
    },
    {
      key: 'hasButton', title: 'Button', dataIndex: 'hasButton', sortable: true, width: 80,
      render: (v: boolean) => v ? <Tag tone="green">Yes</Tag> : <Tag>No</Tag>,
    },
    {
      key: 'isActive', title: 'Status', dataIndex: 'isActive', sortable: true, width: 130,
      render: (active: boolean, r: WaTemplate) => (
        <Space>
          <Switch
            checked={active}
            onCheckedChange={(v) => toggle.mutate({ id: r.id, active: v })}
          />
        </Space>
      ),
    },
    {
      key: 'actions', title: 'Actions', alwaysVisible: true, width: 220,
      render: (_: any, r: WaTemplate) => (
        <Space size="small">
          <Tooltip label="Edit">
            <Button variant="text" size="sm" icon={<Pencil size={16} />} aria-label="Edit" onClick={() => openEdit(r)} />
          </Tooltip>
          <Tooltip label="Send test message">
            <Button variant="text" size="sm" icon={<FlaskConical size={16} />} aria-label="Send test message" onClick={() => setTestFor(r)} />
          </Tooltip>
          <Tooltip label="Show available variables">
            <Button variant="text" size="sm" icon={<Info size={16} />} aria-label="Show available variables" onClick={() => setHelperFor(r.triggerEvent)} />
          </Tooltip>
          <Tooltip label="Delete">
            <Button size="sm" variant="text" icon={<Trash2 size={16} className="text-danger-600 dark:text-danger-500" />} aria-label="Delete"
              onClick={() => Modal.confirm({
                title: 'Delete template?',
                content: `Trigger ${r.triggerEvent} (${r.language}) will no longer fire WhatsApp messages.`,
                okType: 'danger',
                onOk: () => del.mutate(r.id),
              })} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header row — page title + primary actions, above the filters. */}
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>WhatsApp Templates</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={openCreate}>
            New template
          </Button>
        </div>
      </div>

      <Alert
        type="info"
        className="mb-4"
        title="Workflow"
        description={
          <div>
            <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
              <li>Create &amp; submit the template in Meta Business Manager (WhatsApp Manager → Message Templates) and wait for approval.</li>
              <li>Pick the matching <b>trigger</b> here, paste the <b>Meta template name</b>, set the language, and map each <code>{`{{1}}`}</code>, <code>{`{{2}}`}</code>, … position to a variable from the trigger.</li>
              <li>Toggle the row <b>Active</b>. Use the test (🧪) button to send a sample to a real number.</li>
            </ol>
          </div>
        }
      />

      <Card>
        <DataTable
          storageKey="wa-templates"
          rowKey="id"
          loading={isLoading}
          dataSource={templates ?? []}
          columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }}
        />
      </Card>

      <TemplateFormModal
        open={open}
        onClose={() => { setOpen(false); setEditing(null); }}
        editing={editing}
        triggers={triggers ?? []}
        onSubmit={(v) => save.mutate(v)}
        saving={save.isPending}
      />

      <Drawer
        open={!!helperFor}
        onClose={() => setHelperFor(undefined)}
        title="Available variables"
        width={420}
      >
        {helperFor && <VariableHelper trigger={triggers?.find((t) => t.code === helperFor)} />}
      </Drawer>

      <TestSendModal
        template={testFor}
        onClose={() => setTestFor(null)}
      />
    </div>
  );
}

// ====================================================================
// Template create / edit modal
// ====================================================================
function TemplateFormModal({
  open, onClose, editing, triggers, onSubmit, saving,
}: {
  open: boolean;
  onClose: () => void;
  editing: WaTemplate | null;
  triggers: Trigger[];
  onSubmit: (vals: any) => void;
  saving: boolean;
}) {
  const { token } = theme.useToken();
  const [form] = Form.useForm();
  const [selectedTrigger, setSelectedTrigger] = useState<string | undefined>();
  const [variableOrder, setVariableOrder] = useState<string[]>([]);
  const [body, setBody] = useState('');
  const [hasButton, setHasButton] = useState(false);
  const [buttonUsesFirstParam, setButtonUsesFirstParam] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      // Field seeding is handled by the Form's initialValues + key (below), not setFieldsValue here
      // (which raced the Radix-modal remount). This effect keeps only the derived reactive state.
      setSelectedTrigger(editing.triggerEvent);
      setVariableOrder(editing.variableOrder ?? []);
      setBody(editing.bodyPreview ?? '');
      setHasButton(editing.hasButton);
      setButtonUsesFirstParam(editing.buttonUsesFirstParam);
    } else {
      setSelectedTrigger(undefined);
      setVariableOrder([]);
      setBody('');
      setHasButton(false);
      setButtonUsesFirstParam(true);
    }
  }, [open, editing, form]);

  const trigger = triggers.find((t) => t.code === selectedTrigger);
  const availableVars = trigger?.variables ?? [];

  // Auto-suggest button + variables when picking a new trigger
  useEffect(() => {
    if (!trigger || editing) return;
    setHasButton(!!trigger.buttonByDefault);
  }, [trigger, editing]);

  const addVarToBody = (varKey: string) => {
    const next = [...variableOrder, varKey];
    setVariableOrder(next);
    const placeholder = `{{${next.length}}}`;
    setBody((b) => `${b}${placeholder}`);
  };

  const removeLastVar = () => {
    if (variableOrder.length === 0) return;
    const next = variableOrder.slice(0, -1);
    setVariableOrder(next);
    // Remove the {{N}} token wherever it sits (not only at end-of-string),
    // otherwise typing text after it would leave the placeholder behind and
    // desync the body from variableOrder.
    setBody((b) => b.replace(new RegExp(`\\{\\{${variableOrder.length}\\}\\}`), '').replace(/[ \t]+$/, ''));
  };

  const reorderVar = (from: number, to: number) => {
    const next = [...variableOrder];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setVariableOrder(next);
  };

  // Sample render with example values
  const sampleVars: Record<string, string> = {};
  for (const v of availableVars) sampleVars[v.key] = v.example;
  const renderedSample = useMemo(() => {
    let out = body;
    variableOrder.forEach((key, i) => {
      // Replace every occurrence of {{i+1}}, not just the first.
      out = out.split(`{{${i + 1}}}`).join(sampleVars[key] ?? `{{${key}}}`);
    });
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [body, variableOrder, JSON.stringify(sampleVars)]);

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      width={920}
      title={editing ? `Edit · ${editing.metaTemplateName}` : 'New WhatsApp template'}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={() => {
            form.validateFields().then((vals) => {
              onSubmit({ ...vals, bodyPreview: body, variableOrder, hasButton, buttonUsesFirstParam });
            });
          }}>{editing ? 'Update' : 'Create'}</Button>
        </>
      }
    >
      <Form form={form} layout="vertical" key={editing ? `edit-${editing.id}` : 'create'} initialValues={editing ?? undefined}>
        <Row gutter={16}>
          <Col span={10}>
            <Form.Item name="triggerEvent" label="Trigger event" rules={[{ required: true }]}>
              <Select
                placeholder="When should this template fire?"
                showSearch
                optionFilterProp="label"
                value={selectedTrigger}
                onChange={(v) => {
                  setSelectedTrigger(v);
                  setVariableOrder([]);
                  setBody('');
                }}
                disabled={!!editing}
                options={triggers.map((t) => ({
                  value: t.code,
                  label: `[${t.category}] ${t.name}`,
                }))}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item name="metaTemplateName" label="Meta template name" rules={[{ required: true }]}
              extra="Must match the approved template name in Meta Business Manager exactly.">
              <Input placeholder="otp" />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="language" label="Language" initialValue="en" rules={[{ required: true }]}>
              <Select options={LANG_OPTIONS} />
            </Form.Item>
          </Col>
        </Row>

        {trigger && (
          <Alert
            type="info"
            className="mb-3"
            title={trigger.description}
            description={
              <Space wrap size={4} style={{ marginTop: 6 }}>
                <span style={{ marginRight: 6, color: token.colorTextSecondary }}>Available variables:</span>
                {availableVars.map((v) => (
                  <Tag key={v.key} tone="blue" style={{ cursor: 'pointer' }} onClick={() => addVarToBody(v.key)}>
                    <Plus size={16} /> {v.key}
                  </Tag>
                ))}
              </Space>
            }
          />
        )}

        <Row gutter={16}>
          <Col span={14}>
            <Form.Item label="Message body (as approved in Meta)" required>
              <Input.TextArea
                rows={6}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="e.g. Hi {{1}}, your order {{2}} of ₹{{3}} has been placed. Track it in the app."
              />
              <Space style={{ marginTop: 8 }}>
                <Button size="sm" onClick={removeLastVar} disabled={variableOrder.length === 0}>
                  Remove last {`{{N}}`}
                </Button>
                <span style={{ color: token.colorTextTertiary, fontSize: 12 }}>
                  Click a variable tag above to append it. Order matters — {`{{1}}`} → first, {`{{2}}`} → second, etc.
                </span>
              </Space>
            </Form.Item>

            <Form.Item label="Variable order (drag-free reorder via arrows)">
              <Space wrap>
                {variableOrder.length === 0 && <span style={{ color: token.colorTextTertiary }}>No variables yet</span>}
                {variableOrder.map((key, i) => (
                  <Tag key={`${key}-${i}`} tone="blue">
                    {`{{${i + 1}}}`} = {key}{' '}
                    <Button
                      variant="text" size="sm" style={{ padding: 0, margin: '0 4px' }}
                      disabled={i === 0}
                      onClick={() => reorderVar(i, i - 1)}>
                      ↑
                    </Button>
                    <Button
                      variant="text" size="sm" style={{ padding: 0 }}
                      disabled={i === variableOrder.length - 1}
                      onClick={() => reorderVar(i, i + 1)}>
                      ↓
                    </Button>
                  </Tag>
                ))}
              </Space>
            </Form.Item>

            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label="Template has a button (copy code)">
                  <Switch checked={hasButton} onCheckedChange={setHasButton} />
                </Form.Item>
              </Col>
              <Col span={12}>
                {hasButton && (
                  <Form.Item label="Button uses first variable">
                    <Switch
                      checked={buttonUsesFirstParam}
                      onCheckedChange={setButtonUsesFirstParam}
                    />
                  </Form.Item>
                )}
              </Col>
            </Row>

            <Form.Item name="notes" label="Notes (internal only)">
              <Input.TextArea rows={2} placeholder="e.g. Approved by Meta on 6 Jun 2026, used by order pipeline." />
            </Form.Item>
          </Col>

          <Col span={10}>
            <Card title="WhatsApp preview" bodyClassName="bg-[#ECE5DD] p-3">
              <div style={{
                background: '#DCF8C6',
                padding: 10,
                borderRadius: 8,
                whiteSpace: 'pre-wrap',
                fontSize: 14,
                lineHeight: 1.5,
                position: 'relative',
                minHeight: 80,
                color: '#000',
              }}>
                {renderedSample || <span style={{ color: '#888' }}>Body will render here…</span>}
                <div style={{ fontSize: 10, color: '#666', textAlign: 'right', marginTop: 8 }}>
                  ✓✓ 09:14
                </div>
                {hasButton && (
                  <div style={{ marginTop: 8, padding: 6, background: '#fff', textAlign: 'center', borderRadius: 4, color: '#0084ff', fontWeight: 600 }}>
                    <Copy size={16} /> COPY CODE
                  </div>
                )}
              </div>
              <Typography.Text type="secondary" style={{ fontSize: 11, marginTop: 8, display: 'block' }}>
                Sample values shown. Real send substitutes the actual event payload.
              </Typography.Text>
            </Card>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}

// ====================================================================
// Variable helper drawer
// ====================================================================
function VariableHelper({ trigger }: { trigger?: Trigger }) {
  if (!trigger) return <EmptyState title="No data" />;
  return (
    <div>
      <Typography.Title level={5}>{trigger.name}</Typography.Title>
      <Tag tone={CATEGORY_COLOR[trigger.category]}>{trigger.category}</Tag>
      <Typography.Paragraph type="secondary" style={{ marginTop: 8 }}>{trigger.description}</Typography.Paragraph>
      <Divider />
      <Table
        rowKey="key"
        dataSource={trigger.variables}
        pagination={false}
        size="small"
        columns={[
          { title: 'Variable', dataIndex: 'key', render: (v) => <code>{v}</code> },
          { title: 'Description', dataIndex: 'label' },
          { title: 'Example', dataIndex: 'example' },
        ]}
      />
    </div>
  );
}

// ====================================================================
// Test send modal
// ====================================================================
function TestSendModal({ template, onClose }: { template: WaTemplate | null; onClose: () => void }) {
  const [mobile, setMobile] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    if (!template) {
      setMobile(''); setResult(null);
    }
  }, [template]);

  const fire = async () => {
    if (!template) return;
    setSending(true);
    try {
      const r = await request<any>({
        url: `/wa-templates/${template.id}/test`, method: 'POST', data: { mobile },
      });
      setResult(r);
      if (r.ok) message.success('Sent');
      else message.error(r.error ?? 'Send failed');
    } catch (e: any) {
      message.error(e.response?.data?.error?.message ?? 'Failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={!!template}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={template ? `Test "${template.metaTemplateName}"` : ''}
      footer={
        <>
          <Button variant="default" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={sending} disabled={!mobile} onClick={fire}>Send</Button>
        </>
      }
    >
      {template && (
        <div>
          <Typography.Paragraph type="secondary">
            This will send the template with the example values from the trigger.
            Recipient must be on the Meta "allowed numbers" list if your app is in Development mode.
          </Typography.Paragraph>
          <Form layout="vertical">
            <Form.Item label="Recipient mobile" required>
              <Input value={mobile} onChange={(e) => setMobile(e.target.value)} placeholder="9876543210" />
            </Form.Item>
          </Form>
          {result && (
            <Alert
              type={result.ok ? 'success' : 'error'}
              icon={result.ok ? <CheckCircle2 size={16} /> : <Ban size={16} />}
              title={result.ok ? `Sent · message ID ${result.messageId}` : `Failed: ${result.error}`}
            />
          )}
        </div>
      )}
    </Modal>
  );
}
